// Modal de upgrade de assinatura — Story 3.4.
// Trigger: botão "Trocar plano" no detalhe da assinatura.

import * as Dialog from '@radix-ui/react-dialog';
import { ArrowUpRight, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { formatDayBR } from '@control-center/shared';
import type { UpgradePreviewResponse } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Select } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { usePlans } from '../../hooks/use-plans';
import { usePreviewUpgrade, useUpgradeSubscription } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface UpgradeModalProps {
  subscription: SubscriptionDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatBRL(amountString: string): string {
  const n = Number(amountString);
  if (!Number.isFinite(n)) return amountString;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function UpgradeModal({
  subscription,
  open,
  onOpenChange,
}: UpgradeModalProps): JSX.Element | null {
  const [toPlanId, setToPlanId] = useState<string>('');
  const [preview, setPreview] = useState<UpgradePreviewResponse['data'] | null>(null);
  const previewMutation = usePreviewUpgrade();
  const upgradeMutation = useUpgradeSubscription();

  // Lista planos ativos, mesmo tipo do plano atual (filtramos no Select).
  const plansQ = usePlans({ limit: 100 });

  useEffect(() => {
    // Reset state ao abrir/fechar
    if (!open) {
      setToPlanId('');
      setPreview(null);
      previewMutation.reset();
      upgradeMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!subscription) return null;

  const currentPrice = Number(subscription.price ?? '0');
  // Planos elegíveis: ativos + preço > atual + id != atual + mesmo tipo
  // (não dá pra upgrade de 'standalone' pra 'bundle' aqui sem regra extra)
  const eligiblePlans = (plansQ.data?.items ?? []).filter((p) => {
    if (!subscription.plan) return false;
    if (p.id === subscription.plan.id) return false;
    if (p.type !== subscription.plan.type) return false;
    if (Number(p.monthlyPrice) <= currentPrice) return false;
    return true;
  });

  async function handlePreview(planId: string): Promise<void> {
    if (!planId || !subscription) {
      setPreview(null);
      return;
    }
    try {
      const result = await previewMutation.mutateAsync({
        id: subscription.id,
        payload: { to_plan_id: planId },
      });
      setPreview(result);
    } catch (err) {
      setPreview(null);
      toast.error(extractErrorMessage(err, 'Não foi possível calcular a proration.'));
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!toPlanId || !subscription) return;
    try {
      const result = await upgradeMutation.mutateAsync({
        id: subscription.id,
        payload: { to_plan_id: toPlanId },
      });
      const prorationLabel =
        Number(result.prorationAmount) > 0
          ? `Cobrança ponte de ${formatBRL(result.prorationAmount)} criada.`
          : 'Sem cobrança ponte (proration zero — trial ou vencimento hoje).';
      toast.success('Upgrade aplicado.', { description: prorationLabel });
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos aplicar o upgrade.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(560px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-semibold tracking-[-0.005em] text-ink">
                <Sparkles size={16} strokeWidth={1.8} className="text-warn" />
                Trocar de plano (upgrade)
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                {subscription.client.name} · Plano atual:{' '}
                <strong>{subscription.plan?.name ?? '—'}</strong> ({formatBRL(subscription.price)})
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Plano alvo"
              required
              hint="Apenas planos do mesmo tipo, ativos e com preço maior que o atual"
            >
              {({ inputId }) => (
                <Select
                  id={inputId}
                  value={toPlanId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setToPlanId(v);
                    void handlePreview(v);
                  }}
                  disabled={plansQ.isLoading || eligiblePlans.length === 0}
                >
                  <option value="">— selecione —</option>
                  {eligiblePlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {formatBRL(p.monthlyPrice)}/mês
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {eligiblePlans.length === 0 && !plansQ.isLoading && (
              <div className="rounded-[10px] border border-line bg-panel-2 p-3 text-[12px] text-muted">
                Nenhum plano elegível pra upgrade (mesmo tipo + preço maior).
              </div>
            )}

            {/* Preview de proration */}
            {previewMutation.isPending && (
              <div className="rounded-[10px] border border-line bg-panel-2 p-3 text-[12px] text-muted">
                Calculando proration…
              </div>
            )}
            {preview && (
              <div className="rounded-[12px] border border-line bg-panel-2 p-4">
                <div className="mb-3 flex items-center gap-2 text-[12px] uppercase tracking-wider text-muted-3">
                  <ArrowUpRight size={13} strokeWidth={2} />
                  Preview da mudança
                </div>
                <dl className="grid grid-cols-2 gap-y-2 text-[13px]">
                  <dt className="text-muted">De</dt>
                  <dd className="text-right text-ink tnum">{formatBRL(preview.fromPrice)}/mês</dd>
                  <dt className="text-muted">Pra</dt>
                  <dd className="text-right text-ink tnum">{formatBRL(preview.toPrice)}/mês</dd>
                  <dt className="text-muted">Próximo vencimento</dt>
                  <dd className="text-right text-ink tnum">
                    {/* @db.Date (dia puro) → formatDayBR SEM new Date(): o valor
                        chega como meia-noite UTC e `formatBR(new Date(x))` voltava
                        1 dia (mostrava 13/08 quando era 14/08). Ver datetime.spec. */}
                    {formatDayBR(preview.nextBillingDate)}
                  </dd>
                  <dt className="text-muted">Dias restantes do ciclo</dt>
                  <dd className="text-right text-ink tnum">{preview.daysRemaining}</dd>
                  <dt className="font-medium text-ink">Cobrança ponte</dt>
                  <dd className="text-right text-[15px] font-semibold text-ink tnum">
                    {formatBRL(preview.prorationAmount)}
                  </dd>
                </dl>
                {preview.isDuringTrial && (
                  <p className="mt-3 rounded-[8px] border border-warn/30 bg-warn/5 p-2 text-[11px] leading-[1.5] text-ink-2">
                    Cliente em <strong>trial</strong>: proration vai a zero. Plano novo aplica
                    imediato e cobra cheio na próxima recorrência.
                  </p>
                )}
                {Number(preview.prorationAmount) === 0 && !preview.isDuringTrial && (
                  <p className="mt-3 text-[11px] text-muted">
                    Vencimento hoje — sem cobrança ponte. Próxima recorrência cobra o plano novo.
                  </p>
                )}
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton
                type="submit"
                solid
                disabled={!preview || upgradeMutation.isPending || previewMutation.isPending}
              >
                {upgradeMutation.isPending ? 'Aplicando…' : 'Confirmar upgrade'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
